/**
 * The two HTTP primitives BOTH form clients on this surface share: where the engine is, and how
 * a non-auth failure is reported.
 *
 * <p>They live here rather than in either client because the surface now has two — the
 * token-authenticated client form (`formApi.ts`, a magic-link bearer in the request body) and
 * the operator-authenticated staff form (`staffFetcher.ts`, an Entra bearer in the header).
 * "Where is the engine" is one rule with one answer, and a second copy of it is how a
 * cross-origin bug reaches production on exactly one of the two pages.</p>
 */

/**
 * Resolve the engine origin: the build-time `VITE_ENGINE_URL` when set, otherwise the page's own
 * origin.
 *
 * <p>In local dev the variable is unset and Vite proxies `/forms/v0` to the engine, so the page
 * origin is correct. In every deployed environment the SPA and the engine are on DIFFERENT hosts
 * (the forms SWA vs the engine's container app), so the absolute URL is mandatory — a
 * same-origin fetch there would hit the static host and 404.</p>
 *
 * <p>The Node fallback exists only so a module that imports this can be loaded outside a
 * browser (a test runner, an SSR probe) without touching `window`.</p>
 */
export function engineOrigin(): string {
  const envUrl = (import.meta.env as { VITE_ENGINE_URL?: string }).VITE_ENGINE_URL;
  if (envUrl) return envUrl;
  if (typeof window !== "undefined") return window.location.origin;
  return "http://localhost:5050";
}

/**
 * A non-auth HTTP failure. Deliberately carries only the status and the raw body text: no
 * headers, and therefore no credential, can ride out of the client inside an error — the
 * T-15-03-02 token-redaction discipline the operator console's fetcher follows.
 */
export class ApiError extends Error {
  constructor(public readonly status: number, public readonly bodyText: string) {
    super(`subject-surface: HTTP ${status}`);
    this.name = "ApiError";
  }
}

/**
 * Phase 532 — the engine's OWN sentence for a refusal, or `undefined` when the failure carries
 * none.
 *
 * <p>The engine refuses a submission with `ErrorResponse` — `{error_code, message}` — and since
 * this surface became an approval door (ARD-DSL-053) those sentences are the only ones that can
 * be written: "you are not in the role that may lower a band", "this decision differs from the
 * computed one and needs a reason". A generic "something went wrong" over one of those is a page
 * telling an approver to retry something that will be refused identically forever, so the
 * message is carried through and rendered VERBATIM. The renderer never composes it, never
 * matches on the code, and never shows a code it does not understand: the server owns the
 * wording, in one place, for every door.</p>
 *
 * <p>Total by construction — an unreadable body, a body with no `message`, a non-string one, or
 * an error that is not an {@link ApiError} at all all yield `undefined`, which the caller reads
 * as "use the generic copy". Bounded, too: a refusal is a sentence, not a payload, so anything
 * longer than {@link MAX_ERROR_MESSAGE} is dropped rather than poured into the page.</p>
 */
export const MAX_ERROR_MESSAGE = 600;

export function serverMessage(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(err.bodyText) as unknown;
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return undefined;
  const message = (parsed as { message?: unknown }).message;
  if (typeof message !== "string") return undefined;
  const trimmed = message.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_ERROR_MESSAGE ? trimmed : undefined;
}
